import BaseClass from "./BaseClass";
import axios from "axios";
import SharedIntegrationsInstall from "../../graphql/widgets/integrations_install.graphql";

class SharedWidgets extends BaseClass {
  constructor(data) {
    super(data);
    if (this.uri && this.path && !this.broken && !this.complete) {
      let t = [];
      !!this.widgetsJSON || t.push(this.checkUri());
      !!this.manifestJSON || t.push(this.getManifest());
      Promise.all(t).catch(() => {
        this.enabled = false;
        this.broken = true;
        this.save();
      });
    }
  }

  get complete() {
    return !!this.widgetsJSON && !!this.manifestJSON;
  }

  _getJson(url, prop_name) {
    return axios
      .get(url)
      .then(res => {
        // this._log(prop_name, res);
        if (!(res.data instanceof Object)) {
          return Promise.reject(res);
        }
        this._set(this, prop_name, res.data);
      })
      .catch(() => {
        this._delete(this, prop_name);
        // delete this[prop_name];
        return Promise.reject(arguments);
      });
  }

  checkUri() {
    return this._getJson(`${this.uri}/widgets.json`, "widgetsJSON")
      .then(() => {
        if (this.broken === true) {
          this.broken = false;
        }
        try {
          for (let i in this.widgetsJSON[this.code].settings) {
            if (this.widgetsJSON[this.code].settings[i] === "{host}") {
              this.widgetsJSON[this.code].settings[i] = this.uri;
            }
          }
        } catch (e) {}
      })
      .catch(() => {
        this._notify({
          type: "error",
          message: "No access to widgets.json"
        });
        return Promise.reject();
      });
  }

  get service_uri() {
    try {
      return this.uri.replace(/^http[s]{0,1}:\/\//, "");
    } catch (e) {
      return "";
    }
  }

  set service_uri(v) {
    this._set(this, "uri", `https://${v}`);
  }

  get _root() {
    return (this.front_uri || this.uri) + this.path;
  }

  get image() {
    if (!this.logo_main) {
      this._set(this, "logo_main", "data:image/png;base64,");
      this._getLogoMain();
      // return `${this._root}/images/logo_main.png`;
    }
    return this.logo_main;
  }

  get logo_small() {
    return `${this._root}/images/logo_min.png`;
  }

  get repo() {
    try {
      return this.manifestJSON.widget.repository;
    } catch (e) {
      return null;
    }
  }

  get name() {
    try {
      return this._embedded.texts.name[this._locale];
    } catch (e) {
      return `service.${this.code}.name   `;
    }
  }

  get short_description() {
    try {
      return this._embedded.texts.short_description[this._locale];
    } catch (e) {
      return `service.${this.code}.short_description`;
    }
  }

  get description() {
    try {
      return this._embedded.texts.description[this._locale];
    } catch (e) {
      return `service.${this.code}.description`;
    }
  }

  get active() {
    try {
      return !!Object.values(this._embedded.sharedCustomersServices)[0].active;
    } catch (e) {
      // this._log("active.catch");
      return false;
    }
  }

  async getTourData() {
    if (!this.manifestJSON.tour) {
      return;
    }
    if (
      !this.manifestJSON.tour.is_tour ||
      !this.manifestJSON.tour.tour_images ||
      !this.manifestJSON.tour.tour_images.length ||
      !this.manifestJSON.tour.tour_description
    ) {
      delete this.manifestJSON.tour;
      return;
    }
    let locales = [...this.manifestJSON.widget.locale];
    let images = [...(this.manifestJSON.tour.tour_images || [])];

    let files_info = locales.reduce(
      (r, locale) =>
        Object.assign(
          r,
          images.reduce((rr, i) => {
            rr[
              `${i.replace(/^\//, "").replace("{locale}", locale)}`.replace(
                /[^\w\d/.]/g,
                "_"
              )
            ] = `${this._root}/${i
              .replace(/^\//, "")
              .replace("{locale}", locale)}`;
            return rr;
          }, {})
        ),
      {}
    );

    let files = await Promise.all(
      Object.values(files_info).map(v =>
        axios
          .get(v, { responseType: "arraybuffer" })
          .then(res => new Buffer(res.data, "binary").toString("base64"))
          .catch(() => undefined)
      )
    );

    let tour_images = Object.keys(files_info).reduce((reduced, key, index) => {
      if (!files[index]) return reduced;
      let locale_index = Math.trunc(index / images.length);
      reduced[locales[locale_index]] || (reduced[locales[locale_index]] = {});
      reduced[locales[locale_index]][key] = files[index];
      return reduced;
    }, {});

    return tour_images;
  }

  replacementSectionOfTour(tour_images, zip) {
    if (!tour_images) return zip;
    let new_tour_images = undefined;
    Object.keys(tour_images).forEach(key => {
      new_tour_images || (new_tour_images = {});
      new_tour_images[key] = Object.keys(tour_images[key])
        .map(v => `/${v}`)
        .slice(0, 4);
      Object.assign(zip, tour_images[key]);
    });

    !new_tour_images || (this.manifestJSON.tour.tour_images = new_tour_images);
    return zip;
  }

  _getLogoMain() {
    return axios
      .get(`${this._root}/images/logo_main.png`, {
        responseType: "arraybuffer"
      })
      .then(res => {
        let data = new Buffer(res.data, "binary").toString("base64");
        this._set(this, "logo_main", "data:image/png;base64," + data);
        return data;
      });
  }

  async getZipField() {
    let [
      logo,
      logo_main,
      logo_min,
      logo_small,
      logo_medium,
      logo_dp,
      tour_images
    ] = await Promise.all([
      axios
        .get(`${this._root}/images/logo.png`, { responseType: "arraybuffer" })
        .then(res => new Buffer(res.data, "binary").toString("base64")),
      this._getLogoMain(),
      axios
        .get(`${this._root}/images/logo_min.png`, {
          responseType: "arraybuffer"
        })
        .then(res => new Buffer(res.data, "binary").toString("base64")),
      axios
        .get(`${this._root}/images/logo_small.png`, {
          responseType: "arraybuffer"
        })
        .then(res => new Buffer(res.data, "binary").toString("base64")),
      axios
        .get(`${this._root}/images/logo_medium.png`, {
          responseType: "arraybuffer"
        })
        .then(res => new Buffer(res.data, "binary").toString("base64")),
      axios
        .get(`${this._root}/images/logo_dp.png`, {
          responseType: "arraybuffer"
        })
        .then(res => new Buffer(res.data, "binary").toString("base64")),
      this.getTourData()
    ]);
    let prefix = "images/";
    let zip = {};
    zip[`${prefix}logo.png`] = logo;
    zip[`${prefix}logo_min.png`] = logo_min;
    zip[`${prefix}logo_small.png`] = logo_small;
    zip[`${prefix}logo_medium.png`] = logo_medium;
    zip[`${prefix}logo_main.png`] = logo_main;
    zip[`${prefix}logo_dp.png`] = logo_dp;

    this.replacementSectionOfTour(tour_images, zip);

    return zip;
  }

  async installFor(customer, amo, refetch = []) {
    try {
      let result = await this._api.mutate({
        mutation: require("../../graphql/widgets/widgets_install.graphql"),
        variables: {
          install: {
            customer,
            amo,
            widget: Object.assign({ zip: await this.getZipField() }, this)
          }
        }
      });

      return this.handlerInstall(result.data.shared_widgets_install);
    } catch (error) {
      this._notify.error({
        title: "Error",
        message: error
      });
    }
  }

  async installForAsIntegration(customer, amo, type = "full") {
    let zip = await this.getZipField();

    try {
      let result = await this._api.mutate({
        mutation: require("../../graphql/widgets/widgets_install.graphql"),
        variables: {
          install: {
            customer,
            amo,
            widget: Object.assign(
              {
                zip,
                integration_logo: zip["images/logo_main.png"]
              },
              this
            ),
            type
          }
        }
      });
      this._api.query({
        query: require("../../graphql/services/services_list.graphql"),
        variables: {
          customer: customer.uuid,
          amo: amo.uuid
        }
      });

      return this.handlerInstall(result.data.shared_widgets_install);
    } catch (error) {
      this._notify.error({
        title: "Error",
        message: error
      });
    }
  }

  async installIntegrationFor(customer, amo, type) {
    let zip = await this.getZipField();

    try {
      let result = await this._api.mutate({
        mutation: SharedIntegrationsInstall,
        variables: {
          install: {
            customer,
            amo,
            widget: Object.assign(
              {
                zip,
                integration_logo: zip["images/logo_main.png"]
              },
              this
            ),
            type
          }
        }
      });

      return this.handlerInstall(result.data.shared_integrations_install);
    } catch (error) {
      this._notify.error({
        title: "Error",
        message: error
      });
    }
  }

  handlerInstall(result) {
    if (typeof result.error !== "undefined") {
      throw new Error(result.error);
    }
    Object.keys(result).forEach((v, i) => {
      setTimeout(() => {
        this._notify({
          type: v,
          title: v[0].toUpperCase() + v.slice(1),
          message: result[v]
        });
      }, 100 * i);
    });
  }

  static getDataList(variables = {}) {
    // this._log("TYT");
    return this._api.query({
      query: require("../../graphql/widgets/widgets_list.graphql"),
      variables: variables
    });
  }

  getManifest() {
    if (!this.uri || !this.path) {
      return Promise.reject();
    }
    return this._getJson(`${this._root}/manifest.json`, "manifestJSON").catch(
      () => {
        this._notify({
          type: "error",
          message: "No access to manifest.json"
        });
        return Promise.reject();
      }
    );
  }

  toggleWidgetEnabled() {
    this.enabled = !this.enabled;
    return this.save();
  }

  async save() {
    if (!this.uri || !this.path) {
      return Promise.reject();
    }
    if (!this.logo_main) {
      await this._getLogoMain();
    }
    return this.getManifest()
      .catch(() => {})
      .then(res => {
        // this._log(res);
        return this._api
          .mutate({
            mutation: require("../../graphql/widgets/widgets_mutation.graphql"),
            variables: Object.assign({}, this, {
              _embedded: { manifest: this.manifestJSON }
            })
          })
          .then(res => {
            // this._log("res", res);
            Object.assign(this, res.data.shared_widgets);
          });
      });
  }

  delete() {
    return this._api
      .mutate({
        mutation: require("../../graphql/widgets/widgets_mutation.graphql"),
        variables: Object.assign({}, this, { _embedded: { action: "delete" } })
      })
      .then(res => {
        !res.data.shared_widgets._embedded.result ||
          (this._store.state.shared_widgets_list.data = this._store.state.shared_widgets_list.data.filter(
            w => w.uuid !== this.uuid
          ));
      });
  }
}

export default SharedWidgets;
